/**
 * SectionEditor — edits ONE section: its title/description, its ordered questions
 * (add / reorder / duplicate / delete), and wires drag-reordering (VER-109).
 *
 * The question list is managed via setFieldValue + pure array helpers (not a
 * nested FieldArray) so the drag hook can live at the top of this component and
 * stay compliant with the rules of hooks.
 */
import React from "react";
import { Box, Button, Card, CardContent, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { AddOutlined, ContentCopyOutlined, DeleteOutline } from "@mui/icons-material";
import { useFormikContext } from "formik";
import type { Section, SurveyDefinition, SurveyQuestion } from "./survey.types";
import { duplicateQuestion, newQuestion } from "./builder.mock";
import { QuestionEditor } from "./QuestionEditor";
import { ReorderControls, ReorderControlsProps } from "./ReorderControls";
import { arrayInsert, arrayMove, arrayRemove, renumber, useDragReorder } from "./reorder";
import { useFieldError } from "./useFieldError";

export type SectionEditorProps = {
    sectionIndex: number;
    section: Section;
    reorder: Omit<ReorderControlsProps, "label">;
    onDuplicate: () => void;
    onDelete: () => void;
    canDelete: boolean;
};

export const SectionEditor = ({
    sectionIndex,
    section,
    reorder,
    onDuplicate,
    onDelete,
    canDelete,
}: SectionEditorProps): JSX.Element => {
    const { handleChange, handleBlur, setFieldValue } = useFormikContext<SurveyDefinition>();
    const fieldError = useFieldError();
    const basePath = `sections.${sectionIndex}`;
    const questions = section.questions;

    const setQuestions = (next: SurveyQuestion[]) => setFieldValue(`${basePath}.questions`, renumber(next));
    const drag = useDragReorder((from, to) => setQuestions(arrayMove(questions, from, to)));

    const sectionQuestionsError = fieldError(`${basePath}.questions`);

    return (
        <Card variant="outlined" sx={{ borderRadius: (theme) => `${theme.shape.borderRadius}px` }}>
            <CardContent>
                <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                    <ReorderControls label={`section ${sectionIndex + 1}`} {...reorder} />
                    <Box flex={1}>
                        <TextField
                            fullWidth
                            name={`${basePath}.title`}
                            label={`Section ${sectionIndex + 1} title`}
                            value={section.title}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={Boolean(fieldError(`${basePath}.title`))}
                            helperText={fieldError(`${basePath}.title`)}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            name={`${basePath}.description`}
                            label="Section description (optional)"
                            value={section.description ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={{ mt: 1.5 }}
                        />
                    </Box>
                    <Tooltip title="Duplicate section">
                        <IconButton aria-label={`Duplicate section ${sectionIndex + 1}`} onClick={onDuplicate}>
                            <ContentCopyOutlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove section">
                        <span>
                            <IconButton
                                aria-label={`Remove section ${sectionIndex + 1}`}
                                onClick={onDelete}
                                disabled={!canDelete}
                            >
                                <DeleteOutline fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Grid container spacing={2}>
                    {questions.map((question, qi) => (
                        <Grid item xs={12} key={question.id} {...drag.dropZoneProps(qi)}>
                            <QuestionEditor
                                sectionIndex={sectionIndex}
                                questionIndex={qi}
                                question={question}
                                reorder={{
                                    onUp: () => setQuestions(arrayMove(questions, qi, qi - 1)),
                                    onDown: () => setQuestions(arrayMove(questions, qi, qi + 1)),
                                    disableUp: qi === 0,
                                    disableDown: qi === questions.length - 1,
                                    dragHandleProps: drag.handleProps(qi),
                                }}
                                onDuplicate={() =>
                                    setQuestions(arrayInsert(questions, qi + 1, duplicateQuestion(question, qi + 2)))
                                }
                                onDelete={() => setQuestions(arrayRemove(questions, qi))}
                                canDelete={questions.length > 1}
                            />
                        </Grid>
                    ))}
                </Grid>
                <Button
                    sx={{ mt: 2 }}
                    variant="outlined"
                    startIcon={<AddOutlined />}
                    onClick={() => setQuestions([...questions, newQuestion(questions.length + 1)])}
                >
                    Add question
                </Button>
                {typeof sectionQuestionsError === "string" ? (
                    <Typography variant="caption" color="error" display="block" mt={1}>
                        {sectionQuestionsError}
                    </Typography>
                ) : null}
            </CardContent>
        </Card>
    );
};
